import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { 
  Heart, MessageCircle, Send, X, User, Trash2, Image, 
  Smile, ThumbsUp, Laugh, Frown, Angry, Flame, Star, PartyPopper,
  XCircle, Loader2
} from 'lucide-react';

// Reaction types with icons and colors
const REACTIONS = [
  { id: 'like', icon: ThumbsUp, color: '#00d4ff', label: 'Like' },
  { id: 'love', icon: Heart, color: '#ff3366', label: 'Love' },
  { id: 'haha', icon: Laugh, color: '#ffd700', label: 'Haha' },
  { id: 'wow', icon: Star, color: '#ff8c00', label: 'Wow' },
  { id: 'sad', icon: Frown, color: '#4a90d9', label: 'Sad' },
  { id: 'angry', icon: Angry, color: '#e04040', label: 'Angry' },
  { id: 'fire', icon: Flame, color: '#ff6b35', label: 'Fire' },
  { id: 'celebrate', icon: PartyPopper, color: '#9b59b6', label: 'Celebrate' }
];

export default function Social({ navigate }) {
  const { user, profile } = useAuth();
  const [posts, setPosts] = useState([]);
  const [friends, setFriends] = useState([]);
  const [newPost, setNewPost] = useState('');
  const [postImage, setPostImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [postingComment, setPostingComment] = useState(false);
  const [viewingProfile, setViewingProfile] = useState(null);
  const [userPosts, setUserPosts] = useState([]);
  const [userReactions, setUserReactions] = useState({}); // Track user's reactions per post
  const [commentReactions, setCommentReactions] = useState({}); // Track user's reactions per comment
  const [showReactionPicker, setShowReactionPicker] = useState(null); // post id or null
  const [showCommentReactionPicker, setShowCommentReactionPicker] = useState(null); // comment id or null
  const [feedView, setFeedView] = useState('all'); // 'all' or 'mine'
  const imageInputRef = useRef(null);
  const [userAvatars, setUserAvatars] = useState({}); // Cache user avatars

  // Get avatar URL from cache, localStorage, or return null
  const getAvatarUrl = (userId) => {
    // First check our cached avatars from database
    if (userAvatars[userId]) return userAvatars[userId];
    // Then check localStorage
    return localStorage.getItem(`sage_avatar_${userId}`) || null;
  };

  // Fetch user avatars for posts
  const fetchUserAvatars = async (posts) => {
    const userIds = [...new Set(posts.map(p => p.user_id))];
    if (userIds.length === 0) return;
    
    try {
      const { data } = await supabase
        .from('users')
        .select('id, avatar_url')
        .in('id', userIds);
      
      if (data) {
        const avatarMap = {};
        data.forEach(u => {
          if (u.avatar_url) avatarMap[u.id] = u.avatar_url;
        });
        setUserAvatars(prev => ({ ...prev, ...avatarMap }));
      }
    } catch (error) {
      console.error('Error fetching avatars:', error);
    }
  };

  useEffect(() => {
    fetchFriends();
    loadUserReactions();
  }, []);

  useEffect(() => {
    if (friends.length >= 0) {
      fetchPosts();
    }
  }, [friends, feedView]);

  const loadUserReactions = () => {
    // Load user's reactions from localStorage
    const stored = localStorage.getItem(`sage_reactions_${user.id}`);
    if (stored) {
      setUserReactions(JSON.parse(stored));
    }
    const storedComments = localStorage.getItem(`sage_comment_reactions_${user.id}`);
    if (storedComments) {
      setCommentReactions(JSON.parse(storedComments));
    }
  };

  const saveUserReactions = (reactions) => {
    localStorage.setItem(`sage_reactions_${user.id}`, JSON.stringify(reactions));
  };

  const saveCommentReactions = (reactions) => {
    localStorage.setItem(`sage_comment_reactions_${user.id}`, JSON.stringify(reactions));
  };

  const handleCommentReaction = (commentId, reactionId) => {
    const currentReaction = commentReactions[commentId];
    
    if (currentReaction === reactionId) {
      // Remove reaction
      const newCommentReactions = { ...commentReactions };
      delete newCommentReactions[commentId];
      setCommentReactions(newCommentReactions);
      saveCommentReactions(newCommentReactions);
    } else {
      // Add/change reaction
      const newCommentReactions = { ...commentReactions, [commentId]: reactionId };
      setCommentReactions(newCommentReactions);
      saveCommentReactions(newCommentReactions);
    }
    
    setShowCommentReactionPicker(null);
  };

  const fetchFriends = async () => {
    try {
      const { data: data1 } = await supabase
        .from('friendships')
        .select('friend_id')
        .eq('user_id', user.id)
        .eq('status', 'accepted');
      
      const { data: data2 } = await supabase
        .from('friendships')
        .select('user_id')
        .eq('friend_id', user.id)
        .eq('status', 'accepted');
      
      const friendIds = [
        ...(data1?.map(f => f.friend_id) || []),
        ...(data2?.map(f => f.user_id) || []),
        user.id
      ];
      
      setFriends(friendIds);
    } catch (error) {
      console.error('Error fetching friends:', error);
      setFriends([user.id]);
    }
  };

  const fetchPosts = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('social_posts')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (feedView === 'mine') {
        query = query.eq('user_id', user.id);
      }
      
      const { data, error } = await query;

      if (error) {
        console.error('Fetch posts error:', error);
        setPosts([]);
      } else {
        setPosts(data || []);
        // Fetch avatars for all post authors
        if (data && data.length > 0) {
          fetchUserAvatars(data);
        }
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async (postId) => {
    try {
      const { data, error } = await supabase
        .from('social_post_comments')
        .select('*')
        .eq('post_id', postId)
        .order('created_at', { ascending: true });
      
      if (error) {
        // Try alternate table name
        const { data: data2 } = await supabase
          .from('comments')
          .select('*')
          .eq('post_id', postId)
          .order('created_at', { ascending: true });
        setComments(data2 || []);
      } else {
        setComments(data || []);
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
      setComments([]);
    }
  };

  const fetchUserProfile = async (userId) => {
    try {
      const { data: userData } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();
      
      const { data: userPostsData } = await supabase
        .from('social_posts')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      
      setViewingProfile(userData || { id: userId, username: 'User' });
      setUserPosts(userPostsData || []);
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be under 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setPostImage(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const clearPostImage = () => {
    setPostImage(null);
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
  };

  const handlePost = async () => {
    if ((!newPost.trim() && !postImage) || posting) return;

    setPosting(true);
    try {
      const postData = {
        user_id: user.id,
        username: profile?.username || 'User',
        content: newPost.trim(),
        image_url: postImage || null,
        likes: 0,
        reactions: JSON.stringify({})
      };
      
      const { data, error } = await supabase
        .from('social_posts')
        .insert(postData)
        .select()
        .single();

      if (error) {
        console.error('Post error:', error);
        // Try without image_url and reactions if columns don't exist
        const { data: data2, error: error2 } = await supabase
          .from('social_posts')
          .insert({
            user_id: user.id,
            username: profile?.username || 'User',
            content: newPost.trim() || '[Image]',
            likes: 0
          })
          .select()
          .single();
        
        if (!error2 && data2) {
          // Store image in localStorage if column doesn't exist
          if (postImage) {
            localStorage.setItem(`sage_post_image_${data2.id}`, postImage);
          }
          setPosts([{ ...data2, image_url: postImage }, ...posts]);
        }
      } else {
        setPosts([data, ...posts]);
      }
      setNewPost('');
      clearPostImage();
    } catch (error) {
      console.error('Error posting:', error);
    } finally {
      setPosting(false);
    }
  };

  // Get post image - check localStorage if not in post data
  const getPostImage = (post) => {
    if (post.image_url) return post.image_url;
    return localStorage.getItem(`sage_post_image_${post.id}`);
  };

  const handleReaction = async (postId, reactionId) => {
    const currentReaction = userReactions[postId];
    const post = posts.find(p => p.id === postId);
    if (!post) return;
    
    // Parse current reactions
    let postReactions = {};
    try {
      postReactions = post.reactions ? JSON.parse(post.reactions) : {};
    } catch {
      postReactions = {};
    }
    
    // Update reactions
    if (currentReaction === reactionId) {
      // Remove reaction
      if (postReactions[reactionId] > 0) {
        postReactions[reactionId]--;
      }
      const newUserReactions = { ...userReactions };
      delete newUserReactions[postId];
      setUserReactions(newUserReactions);
      saveUserReactions(newUserReactions);
    } else {
      // Remove old reaction if exists
      if (currentReaction && postReactions[currentReaction] > 0) {
        postReactions[currentReaction]--;
      }
      // Add new reaction
      postReactions[reactionId] = (postReactions[reactionId] || 0) + 1;
      const newUserReactions = { ...userReactions, [postId]: reactionId };
      setUserReactions(newUserReactions);
      saveUserReactions(newUserReactions);
    }
    
    // Calculate total likes
    const totalLikes = Object.values(postReactions).reduce((sum, count) => sum + count, 0);
    
    // Optimistic update
    setPosts(prev => prev.map(p => 
      p.id === postId 
        ? { ...p, reactions: JSON.stringify(postReactions), likes: totalLikes }
        : p
    ));
    
    setShowReactionPicker(null);

    // Update in database
    try {
      await supabase
        .from('social_posts')
        .update({ reactions: JSON.stringify(postReactions), likes: totalLikes })
        .eq('id', postId);
    } catch (error) {
      console.error('Error updating reaction:', error);
    }
  };

  const handleComment = async () => {
    if (!newComment.trim() || !selectedPost || postingComment) return;

    setPostingComment(true);
    try {
      // Try social_post_comments first
      let result = await supabase.from('social_post_comments').insert({
        post_id: selectedPost.id,
        user_id: user.id,
        username: profile?.username || 'User',
        content: newComment.trim()
      }).select().single();

      if (result.error) {
        // Try comments table as fallback
        result = await supabase.from('comments').insert({
          post_id: selectedPost.id,
          user_id: user.id,
          username: profile?.username || 'User',
          content: newComment.trim()
        }).select().single();
      }

      if (result.data) {
        setComments([...comments, result.data]);
        setNewComment('');
      } else if (result.error) {
        console.error('Comment error:', result.error);
        // Optimistic add for UI
        setComments([...comments, {
          id: Date.now(),
          post_id: selectedPost.id,
          user_id: user.id,
          username: profile?.username || 'User',
          content: newComment.trim(),
          created_at: new Date().toISOString()
        }]);
        setNewComment('');
      }
    } catch (error) {
      console.error('Error commenting:', error);
    } finally {
      setPostingComment(false);
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await supabase.from('social_posts').delete().eq('id', postId);
      setPosts(prev => prev.filter(p => p.id !== postId));
    } catch (error) {
      console.error('Error deleting post:', error);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h`;
    return date.toLocaleDateString();
  };

  const getReactionSummary = (post) => {
    let reactions = {};
    try {
      reactions = post.reactions ? JSON.parse(post.reactions) : {};
    } catch {
      reactions = {};
    }
    
    const sorted = Object.entries(reactions)
      .filter(([_, count]) => count > 0)
      .sort((a, b) => b[1] - a[1]);
    
    return sorted.slice(0, 3);
  };

  // Profile View Modal
  if (viewingProfile) {
    const profileAvatar = getAvatarUrl(viewingProfile.id) || viewingProfile.avatar_url;
    
    return (
      <div className="min-h-screen bg-[#08080c] pb-20" data-testid="profile-view">
        <div className="p-4 border-b border-[#1a1a24] flex items-center justify-between">
          <button
            onClick={() => setViewingProfile(null)}
            className="text-[#4a4a60] hover:text-[#00d4ff] text-xs font-mono"
          >
            ← Back to Feed
          </button>
        </div>
        
        <div className="p-4">
          <div className="flex items-center gap-4 mb-6">
            {profileAvatar ? (
              <img 
                src={profileAvatar} 
                alt={viewingProfile.username}
                className="w-16 h-16 rounded-lg object-cover border border-[#00d4ff]/30"
              />
            ) : (
              <div className="w-16 h-16 border border-[#00d4ff]/30 rounded-lg flex items-center justify-center bg-[#00d4ff]/5">
                <span className="font-mono text-xl text-[#00d4ff]">
                  {viewingProfile.username?.[0]?.toUpperCase() || '?'}
                </span>
              </div>
            )}
            <div>
              <h2 className="text-lg text-[#00d4ff]">{viewingProfile.username}</h2>
              <p className="text-xs text-[#4a4a60]">{userPosts.length} posts</p>
              {viewingProfile.id === user.id && (
                <span className="text-[9px] text-[#00ff88] uppercase">Your Profile</span>
              )}
            </div>
          </div>

          <p className="font-mono text-[10px] text-[#00d4ff] uppercase tracking-wider mb-3">Posts</p>
          <div className="space-y-3">
            {userPosts.length === 0 ? (
              <p className="text-center text-[#4a4a60] text-xs font-mono py-8">No posts yet</p>
            ) : (
              userPosts.map(post => (
                <div key={post.id} className="bg-[#0d0d12] border border-[#1a1a24] p-4">
                  <p className="text-xs text-[#e8e8f0] whitespace-pre-wrap">{post.content}</p>
                  {post.image_url && (
                    <img 
                      src={post.image_url} 
                      alt="Post" 
                      className="mt-3 max-h-48 rounded border border-[#1a1a24]"
                    />
                  )}
                  <div className="flex items-center gap-4 mt-3 text-[#4a4a60]">
                    <span className="text-[10px]">{formatDate(post.created_at)}</span>
                    <span className="text-[10px] flex items-center gap-1">
                      <Heart className="w-3 h-3" /> {post.likes || 0}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    );
  }

  // Comments Modal
  if (selectedPost) {
    return (
      <div className="min-h-screen bg-[#08080c] pb-20" data-testid="comments-view">
        <div className="p-4 border-b border-[#1a1a24] flex items-center justify-between">
          <span className="font-mono text-xs text-[#00d4ff] uppercase tracking-wider">Comments</span>
          <button
            onClick={() => { setSelectedPost(null); setComments([]); }}
            className="p-2 hover:bg-[#111118]"
          >
            <X className="w-4 h-4 text-[#4a4a60]" />
          </button>
        </div>

        {/* Original Post */}
        <div className="p-4 border-b border-[#1a1a24]">
          <div className="flex items-center gap-3 mb-2">
            {getAvatarUrl(selectedPost.user_id) ? (
              <img 
                src={getAvatarUrl(selectedPost.user_id)}
                alt={selectedPost.username}
                className="w-10 h-10 rounded-lg object-cover border border-[#00d4ff]/30"
              />
            ) : (
              <div className="w-10 h-10 border border-[#00d4ff]/30 rounded-lg flex items-center justify-center bg-[#00d4ff]/5">
                <span className="font-mono text-sm text-[#00d4ff]">
                  {selectedPost.username?.[0]?.toUpperCase() || '?'}
                </span>
              </div>
            )}
            <div>
              <p className="text-xs text-[#00d4ff]">{selectedPost.username}</p>
              <p className="text-[9px] text-[#4a4a60]">{formatDate(selectedPost.created_at)}</p>
            </div>
          </div>
          <p className="text-sm text-[#e8e8f0] whitespace-pre-wrap">{selectedPost.content && selectedPost.content !== '[Image]' ? selectedPost.content : ''}</p>
          {getPostImage(selectedPost) && (
            <img 
              src={getPostImage(selectedPost)} 
              alt="Post" 
              className="mt-3 max-h-48 rounded border border-[#1a1a24]"
            />
          )}
        </div>

        {/* Comments List */}
        <div className="p-4 space-y-3 pb-32">
          {comments.length === 0 ? (
            <p className="text-center text-[#4a4a60] text-xs font-mono py-4">No comments yet</p>
          ) : (
            comments.map(comment => {
              const commentAvatar = getAvatarUrl(comment.user_id);
              const userCommentReaction = commentReactions[comment.id];
              
              return (
                <div key={comment.id} className="flex gap-3">
                  {commentAvatar ? (
                    <img 
                      src={commentAvatar}
                      alt={comment.username}
                      className="w-7 h-7 rounded-lg object-cover border border-[#00d4ff]/30 flex-shrink-0"
                    />
                  ) : (
                    <div className="w-7 h-7 border border-[#1a1a24] rounded-lg flex items-center justify-center flex-shrink-0 bg-[#00d4ff]/5">
                      <span className="font-mono text-[9px] text-[#00d4ff]">
                        {comment.username?.[0]?.toUpperCase() || '?'}
                      </span>
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="bg-[#0d0d12] border border-[#1a1a24] p-3 rounded">
                      <p className="text-[10px] text-[#00d4ff] mb-1">{comment.username}</p>
                      <p className="text-xs text-[#e8e8f0]">{comment.content}</p>
                    </div>
                    
                    {/* Comment Actions */}
                    <div className="flex items-center gap-3 mt-1 ml-1 relative">
                      <span className="text-[9px] text-[#4a4a60]">{formatDate(comment.created_at)}</span>
                      
                      {/* Reaction button */}
                      <div className="relative">
                        <button
                          onClick={() => setShowCommentReactionPicker(showCommentReactionPicker === comment.id ? null : comment.id)}
                          className={`text-[9px] font-mono uppercase hover:text-[#00d4ff] transition-colors ${
                            userCommentReaction ? '' : 'text-[#4a4a60]'
                          }`}
                          style={userCommentReaction ? { color: REACTIONS.find(r => r.id === userCommentReaction)?.color } : {}}
                        >
                          {userCommentReaction ? REACTIONS.find(r => r.id === userCommentReaction)?.label : 'React'}
                        </button>
                        
                        {/* Reaction Picker for comment */}
                        {showCommentReactionPicker === comment.id && (
                          <div className="absolute bottom-full left-0 mb-1 bg-[#0d0d12] border border-[#1a1a24] rounded-lg p-1.5 flex gap-0.5 shadow-xl z-10">
                            {REACTIONS.map(reaction => (
                              <button
                                key={reaction.id}
                                onClick={() => handleCommentReaction(comment.id, reaction.id)}
                                className={`p-1.5 rounded hover:bg-[#111118] transition-all hover:scale-125 ${
                                  userCommentReaction === reaction.id ? 'bg-[#111118] scale-110' : ''
                                }`}
                                title={reaction.label}
                                style={{ color: reaction.color }}
                              >
                                <reaction.icon className="w-4 h-4" />
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Comment Input */}
        <div className="fixed bottom-14 left-0 right-0 p-4 bg-[#08080c] border-t border-[#1a1a24]">
          <div className="max-w-md mx-auto flex gap-2">
            <input
              type="text"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleComment()}
              placeholder="Write a comment..."
              className="flex-1 bg-[#0d0d12] border border-[#1a1a24] text-[#e8e8f0] px-4 py-3 text-xs font-mono focus:border-[#00d4ff]/50 outline-none rounded"
              data-testid="comment-input"
            />
            <button
              onClick={handleComment}
              disabled={!newComment.trim() || postingComment}
              className="border border-[#00d4ff]/50 text-[#00d4ff] px-4 py-3 hover:bg-[#00d4ff]/10 disabled:opacity-30 rounded flex items-center justify-center min-w-[48px]"
              data-testid="send-comment-btn"
            >
              {postingComment ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#08080c] flex items-center justify-center pb-20">
        <div className="text-[#00d4ff] font-mono text-sm animate-pulse">Loading...</div>
      </div>
    );
  }

  return (
    <div className="bg-[#08080c] pb-20" data-testid="social-screen">
      {/* Feed Toggle */}
      <div className="flex border-b border-[#1a1a24]">
        <button
          onClick={() => setFeedView('all')}
          className={`flex-1 py-3 font-mono text-[10px] uppercase tracking-wider transition-all ${
            feedView === 'all'
              ? 'text-[#00d4ff] border-b-2 border-[#00d4ff] bg-[#00d4ff]/5'
              : 'text-[#4a4a60] hover:text-[#7a7a9a]'
          }`}
          data-testid="feed-all-btn"
        >
          All Posts
        </button>
        <button
          onClick={() => setFeedView('mine')}
          className={`flex-1 py-3 font-mono text-[10px] uppercase tracking-wider transition-all ${
            feedView === 'mine'
              ? 'text-[#00d4ff] border-b-2 border-[#00d4ff] bg-[#00d4ff]/5'
              : 'text-[#4a4a60] hover:text-[#7a7a9a]'
          }`}
          data-testid="feed-mine-btn"
        >
          My Posts
        </button>
      </div>

      {/* Compose */}
      <div className="m-4 p-4 bg-[#0d0d12] border border-[#00d4ff]/30 rounded-xl hover:border-[#00d4ff]/50 transition-colors" data-testid="compose-box">
        <div className="flex gap-3">
          {getAvatarUrl(user.id) ? (
            <img 
              src={getAvatarUrl(user.id)} 
              alt="You"
              className="w-10 h-10 rounded-lg object-cover border border-[#00d4ff]/30 flex-shrink-0"
            />
          ) : (
            <div className="w-10 h-10 border border-[#00d4ff]/30 rounded-lg flex items-center justify-center bg-[#00d4ff]/5 flex-shrink-0">
              <span className="font-mono text-sm text-[#00d4ff]">
                {profile?.username?.[0]?.toUpperCase() || '?'}
              </span>
            </div>
          )}
          <div className="flex-1">
            <textarea
              value={newPost}
              onChange={(e) => setNewPost(e.target.value)}
              placeholder="What's happening?"
              className="w-full bg-transparent text-[#e8e8f0] text-sm resize-none outline-none placeholder:text-[#e8e8f0]/60"
              rows={2}
              data-testid="post-textarea"
            />
            
            {/* Image Preview */}
            {postImage && (
              <div className="relative mt-2 inline-block">
                <img 
                  src={postImage} 
                  alt="Preview" 
                  className="max-h-32 rounded border border-[#00d4ff]/30"
                />
                <button
                  onClick={clearPostImage}
                  className="absolute -top-2 -right-2 bg-[#ff3366] rounded-full p-1 hover:bg-[#ff3366]/80"
                >
                  <XCircle className="w-4 h-4 text-white" />
                </button>
              </div>
            )}
            
            <div className="flex justify-between items-center mt-3 pt-3 border-t border-[#1a1a24]">
              {/* Hidden file input */}
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                className="hidden"
              />
              
              {/* Image upload button */}
              <button
                onClick={() => imageInputRef.current?.click()}
                className="p-2 border border-[#00d4ff]/50 text-[#00d4ff] hover:border-[#00d4ff] hover:bg-[#00d4ff]/10 transition-colors rounded"
                data-testid="add-image-btn"
              >
                <Image className="w-4 h-4" />
              </button>
              
              <button
                onClick={handlePost}
                disabled={(!newPost.trim() && !postImage) || posting}
                className="bg-[#0d0d12] border border-[#00d4ff] text-[#00d4ff] font-mono text-xs font-bold uppercase tracking-wider px-6 py-2.5 hover:bg-[#00d4ff]/20 hover:shadow-[0_0_15px_rgba(0,212,255,0.3)] disabled:opacity-40 disabled:hover:bg-[#0d0d12] transition-all rounded-lg flex items-center gap-2"
                data-testid="post-btn"
              >
                {posting && <Loader2 className="w-3 h-3 animate-spin" />}
                Post
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Posts */}
      <div data-testid="posts-list">
        {posts.length === 0 ? (
          <div className="text-center py-12 px-4">
            <MessageCircle className="w-10 h-10 text-[#1a1a24] mx-auto mb-4" />
            <p className="text-[#4a4a60] text-xs font-mono">
              {feedView === 'mine' ? 'You have no posts yet' : 'No posts yet'}
            </p>
            <p className="text-[#4a4a60] text-[10px] mt-1">
              {feedView === 'mine' ? 'Create your first post above' : 'Be the first to post something'}
            </p>
          </div>
        ) : (
          posts.map((post) => {
            const postAvatar = getAvatarUrl(post.user_id);
            const userReaction = userReactions[post.id];
            const reactionSummary = getReactionSummary(post);
            
            return (
              <div
                key={post.id}
                className="p-4 border-b border-[#1a1a24]"
                data-testid={`post-${post.id}`}
              >
                <div className="flex gap-3">
                  {/* Avatar - clickable for profile */}
                  <button
                    onClick={() => fetchUserProfile(post.user_id)}
                    className="flex-shrink-0"
                    data-testid={`view-profile-${post.user_id}`}
                  >
                    {postAvatar ? (
                      <img 
                        src={postAvatar} 
                        alt={post.username}
                        className="w-10 h-10 rounded-lg object-cover border border-[#00d4ff]/30 hover:border-[#00d4ff]/60 transition-colors"
                      />
                    ) : (
                      <div className="w-10 h-10 border border-[#00d4ff]/30 rounded-lg flex items-center justify-center bg-[#00d4ff]/5 hover:border-[#00d4ff]/60 transition-colors">
                        <span className="font-mono text-sm text-[#00d4ff]">
                          {post.username?.[0]?.toUpperCase() || '?'}
                        </span>
                      </div>
                    )}
                  </button>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <button
                        onClick={() => fetchUserProfile(post.user_id)}
                        className="text-xs text-[#00d4ff] font-medium hover:underline transition-colors"
                      >
                        {post.username}
                      </button>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-[#4a4a60]">
                          {formatDate(post.created_at)}
                        </span>
                        {post.user_id === user.id && (
                          <button
                            onClick={() => handleDelete(post.id)}
                            className="p-1 hover:bg-[#ff3366]/10 text-[#4a4a60] hover:text-[#ff3366] transition-colors rounded"
                            data-testid={`delete-post-${post.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>

                    <p className="text-sm text-[#e8e8f0] mt-2 whitespace-pre-wrap">
                      {post.content && post.content !== '[Image]' ? post.content : ''}
                    </p>
                    
                    {/* Post Image */}
                    {getPostImage(post) && (
                      <img 
                        src={getPostImage(post)} 
                        alt="Post" 
                        className="mt-3 max-h-64 rounded-lg border border-[#1a1a24] cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => window.open(getPostImage(post), '_blank')}
                      />
                    )}

                    {/* Reaction Summary */}
                    {reactionSummary.length > 0 && (
                      <div className="flex items-center gap-1 mt-2">
                        {reactionSummary.map(([reactionId, count]) => {
                          const reaction = REACTIONS.find(r => r.id === reactionId);
                          if (!reaction) return null;
                          return (
                            <div 
                              key={reactionId}
                              className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px]"
                              style={{ backgroundColor: `${reaction.color}15`, color: reaction.color }}
                            >
                              <reaction.icon className="w-3 h-3" />
                              <span>{count}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-4 mt-3 relative">
                      {/* Reaction Button */}
                      <div className="relative">
                        <button
                          onClick={() => setShowReactionPicker(showReactionPicker === post.id ? null : post.id)}
                          className={`flex items-center gap-1.5 transition-colors group ${
                            userReaction 
                              ? '' 
                              : 'text-[#4a4a60] hover:text-[#00d4ff]'
                          }`}
                          style={userReaction ? { color: REACTIONS.find(r => r.id === userReaction)?.color } : {}}
                          data-testid={`reaction-btn-${post.id}`}
                        >
                          {userReaction ? (
                            (() => {
                              const r = REACTIONS.find(r => r.id === userReaction);
                              return r ? <r.icon className="w-4 h-4" /> : <ThumbsUp className="w-4 h-4" />;
                            })()
                          ) : (
                            <ThumbsUp className="w-4 h-4" />
                          )}
                          <span className="text-[10px]">{post.likes || 0}</span>
                        </button>
                        
                        {/* Reaction Picker */}
                        {showReactionPicker === post.id && (
                          <div className="absolute bottom-full left-0 mb-2 bg-[#0d0d12] border border-[#1a1a24] rounded-xl p-2 flex gap-1 shadow-xl z-10">
                            {REACTIONS.map(reaction => (
                              <button
                                key={reaction.id}
                                onClick={() => handleReaction(post.id, reaction.id)}
                                className={`p-2 rounded-lg hover:bg-[#111118] transition-all hover:scale-125 ${
                                  userReaction === reaction.id ? 'bg-[#111118] scale-110' : ''
                                }`}
                                title={reaction.label}
                                style={{ color: reaction.color }}
                              >
                                <reaction.icon className="w-5 h-5" />
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      <button
                        onClick={() => {
                          setSelectedPost(post);
                          fetchComments(post.id);
                        }}
                        className="flex items-center gap-1.5 text-[#4a4a60] hover:text-[#00d4ff] transition-colors"
                        data-testid={`comment-btn-${post.id}`}
                      >
                        <MessageCircle className="w-4 h-4" />
                        <span className="text-[10px]">Comment</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
      
      {/* Click outside to close reaction picker */}
      {(showReactionPicker || showCommentReactionPicker) && (
        <div 
          className="fixed inset-0 z-0" 
          onClick={() => {
            setShowReactionPicker(null);
            setShowCommentReactionPicker(null);
          }}
        />
      )}
    </div>
  );
}
